import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Icon } from '@/ui';

/** Show after the user has scrolled roughly one short viewport section. */
const SHOW_AFTER_PX = 240;

/**
 * Floating control that returns to the top of the page. On mobile it sits just
 * above the bottom nav; on desktop it anchors to the viewport corner.
 */
export function BackToTop() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const update = () => {
      setVisible(window.scrollY > SHOW_AFTER_PX);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  return (
    <button
      type="button"
      aria-label={t('Back to top', { defaultValue: 'Back to top' })}
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={cn(
        'fixed right-3 z-40 flex size-11 items-center justify-center rounded-full lg:right-6',
        // Mobile: clear the bottom nav (80px) + gap. Desktop: viewport corner.
        'bottom-[calc(5rem+0.75rem)] lg:bottom-6',
        'border border-border-subtle/50 bg-bg-surface/70 text-icon-default',
        'shadow-md backdrop-blur-sm transition-[opacity,transform] duration-200',
        'hover:bg-bg-surface/85 focus-visible:outline-2 focus-visible:outline-offset-2',
        'focus-visible:outline-action-primary',
        visible
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-2 opacity-0',
      )}
    >
      <Icon name="arrow-down" size={12} className="rotate-180" />
    </button>
  );
}
